import React, { useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { palette } from '../../theme/colors';

interface Invoice {
  id: string;
  invoiceNumber: string;
  status: string;
  total: number;
  amountPaid: number;
  amountDue: number;
  dueDate: string;
  createdAt: string;
}

interface PortalContext {
  isAuthenticated: boolean;
}

const fetchMyInvoices = async (orgSlug: string, token: string): Promise<Invoice[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/my-invoices`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch invoices');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : data.data || [];
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'paid':
      return palette.success;
    case 'sent':
      return palette.primary;
    case 'draft':
      return palette.textMuted;
    case 'overdue':
      return palette.error;
    case 'partially_paid':
      return palette.warning;
    default:
      return palette.textMuted;
  }
};

export const B2BInvoicesPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useOutletContext<PortalContext>();
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const { data: invoices, isLoading, error } = useQuery({
    queryKey: ['b2b-my-invoices', orgSlug],
    queryFn: () => fetchMyInvoices(orgSlug!, token!),
    enabled: !!orgSlug && !!token,
  });

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        My Invoices
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View your invoices and payment status
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load invoices. Please try again.
        </Alert>
      )}

      {!isLoading && invoices?.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <ReceiptIcon sx={{ fontSize: 80, color: palette.textMuted, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No invoices yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invoices will appear here after quotes are accepted
          </Typography>
        </Card>
      )}

      {invoices && invoices.length > 0 && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: palette.sky }}>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Paid</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{invoice.invoiceNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {invoice.total.toFixed(2)} DZD
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color={palette.success}>
                        {invoice.amountPaid.toFixed(2)} DZD
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color={invoice.amountDue > 0 ? palette.error : palette.success}>
                        {invoice.amountDue.toFixed(2)} DZD
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(invoice.dueDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status.replace('_', ' ').toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: `${getStatusColor(invoice.status)}20`,
                          color: getStatusColor(invoice.status),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(invoice.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};
